// Types
import { Me_me } from 'api/auth/types/Me';
import { MeProfileSetup_me } from 'api/auth/types/MeProfileSetup';
import {
  EarthCoordinateInput,
  StoreAthleticLevel,
  StoreCareerStatus,
} from 'api/graphql-global-types';
import { OTPType, OTPValidation } from 'context/OnboardingContext';
import { UseFormMethods } from 'react-hook-form';

export type ImageAddedEvent = { image: File; previewImage: string };

export type QuickProfileSetupFormState = {
  formValid: boolean;
  bioData: string | null;
  storeName: string | null;
  imageData: ImageAddedEvent | null;
  sportsIds: number[] | null;
  hometown: string;
  hometownState: string;
  hometownCountry: string;
  country: string;
  hometownLocation: EarthCoordinateInput | null;
  state: string;
  location: EarthCoordinateInput | null;
  athleticLevel: StoreAthleticLevel | null;
  careerStatus: StoreCareerStatus | null;
  city: string | null;
  phoneNumberE164: string | null;
  phoneOptIn: boolean | null;
  gymRole: string | null;
  email: string;
  facebook?: string;
  twitter?: string;
  instagram?: string;
  tiktok?: string;
  youtube?: string;
  instagramFollowing: number | null;
  facebookFollowing: number | null;
  tiktokFollowing: number | null;
  twitterFollowing: number | null;
  youtubeFollowing: number | null;
};

export const shouldRevalidateOtp = (
  email: string,
  phone: string,
  store?: MeProfileSetup_me | Me_me,
  isOnboarding = false
): { emailNeedsVerification: boolean; phoneNeedsVerification: boolean } => {
  const emailChanged = email !== store?.email;
  const phoneChanged = phone !== store?.phoneNumber;
  let emailNeedsVerification = store?.isEmailVerified && emailChanged;
  let phoneNeedsVerification = store?.isPhoneVerified && phoneChanged;

  /**
   * For onboarding, we need to check is verified and is changed
   * but for editing, we only need to check if its changed and was previously verified
   */
  if (isOnboarding) {
    emailNeedsVerification = !store?.isEmailVerified || emailNeedsVerification;
    phoneNeedsVerification = !store?.isPhoneVerified || phoneNeedsVerification;
  }

  return {
    emailNeedsVerification: !!emailNeedsVerification,
    phoneNeedsVerification: !!phoneNeedsVerification,
  };
};

/**
 * Adds OTP verification fields to the form data based on validation type
 * @param {OTPValidation | null} otpValidation - The OTP validation object containing type and ID
 * @returns {Record<string, string>} Object containing OTP verification fields if validation exists
 */
export const addOtpFields = (
  otpValidation: OTPValidation | null
): Record<string, string> => {
  return otpValidation &&
    [OTPType.Phone, OTPType.Email].includes(otpValidation.type)
    ? {
        [otpValidation.type === OTPType.Email
          ? 'emailOtpVerificationId'
          : 'phoneOtpVerificationId']: otpValidation.id,
      }
    : {};
};

/**
 * Utility function to persist form fields when navigating between steps
 * @param methods - React Hook Form methods object
 * @param currentData - Current data object to merge with
 * @param fieldsToPersist - Array of field names to persist from the form
 * @returns Updated data object with persisted fields
 */
export const persistFormFields = <T extends Record<string, any>>(
  methods: UseFormMethods<any>,
  currentData: T | undefined | null,
  fieldsToPersist: (keyof T)[]
): T => {
  if (!currentData) {
    return {} as T;
  }

  const persistedFields = fieldsToPersist.reduce((acc, field) => {
    const value = methods.getValues(field as string);
    // Including falsy js values (0, '', etc)
    if (value !== undefined && value !== null) {
      acc[field] = value;
    }
    return acc;
  }, {} as Partial<T>);

  return {
    ...currentData,
    ...persistedFields,
  };
};
